import { DOOR_TYPES, createDoor } from './doors';
import { getGuardianCounterDamage } from './sectorEnemies';

const STRONG_CLEAR_MARGIN = 15;
export const HARD_PORTAL_COST = 3;

// Include starter chambers saved before the Anomaly route was made available.
export const isStarterPortalChamber = (game) =>
  game.floor === 2 && game.portalRoutes?.intro === true;

export const validPortalRoutes = (game) => {
  const routes = game.portalRoutes;
  return (
    !!routes &&
    routes.floor === game.floor &&
    ['recovery', 'clear', 'strong'].includes(routes.outcome) &&
    Array.isArray(routes.normal) &&
    routes.normal.length === 2 &&
    new Set([...routes.normal, routes.hard]).size === 3 &&
    [...routes.normal, routes.hard].every(
      (id) => !!createDoor(id, game.floor)
    ) &&
    typeof routes.hardUnlocked === 'boolean' &&
    Number.isFinite(routes.unlockTarget) &&
    Number.isFinite(routes.lastRoll)
  );
};

export const getRouteOutcome = (result) =>
  !result?.success
    ? 'recovery'
    : (result.attacks ?? 1) === 1
    ? 'strong'
    : 'clear';

const drawEncounter = (types, used, random) => {
  const pool = DOOR_TYPES.filter(({ id }) => types.includes(id)).flatMap(
    ({ encounters }) => encounters.filter(({ id }) => !used.has(id))
  );
  const encounter =
    pool[Math.min(pool.length - 1, Math.floor(random() * pool.length))];
  used.add(encounter.id);
  return encounter.id;
};

// The choices are drawn once when the chamber opens, then saved with the run.
export const generatePortalRoutes = (game, floor, random = Math.random) => {
  const intro = game.breachIntro && game.floor === 1;
  const outcome = intro ? 'clear' : getRouteOutcome(game.lastResult);
  const used = new Set((game.history || []).map(({ roomId }) => roomId));
  const safe = drawEncounter(['recovery', 'supply'], used, random);
  const standard = drawEncounter(
    outcome === 'recovery' ? ['salvage', 'sanctuary'] : ['treasure'],
    used,
    random
  );
  const hard = drawEncounter(
    intro ? ['anomaly'] : ['trial', 'anomaly'],
    used,
    random
  );
  return {
    floor,
    outcome,
    intro,
    normal: [safe, standard],
    hard,
    hardUnlocked: intro || outcome === 'strong',
    unlockTarget: intro ? 0 : game.lastResult.target + STRONG_CLEAR_MARGIN,
    lastRoll: intro ? 0 : game.lastResult.effectiveRoll,
    hardPaid: false,
    unlockSeen: false,
  };
};

export const getPortalChoices = (game) => {
  const routes = game.portalRoutes;
  if (!routes || routes.floor !== game.floor) return [];
  const hardAvailable = routes.hardUnlocked || isStarterPortalChamber(game);
  if (game.floor === 5)
    return [
      {
        ...createDoor(routes.normal[0], game.floor),
        name: 'The Rift Warden',
        color: '#ffb66d',
        kind: 'Trial',
        target: 500,
        damage: 2,
        shards: 4,
        heal: 0,
        bonusRelic: true,
        description:
          'The 500 HP Warden guards this sector. Guaranteed random relic. Shards by attacks: 1–3 → 8; 4–6 → 6; 7+ → 4. Retaliation grows from 2 to 4 HP.',
        routeClass: 'warden',
        locked: false,
        entryCost: 0,
        canPurchase: false,
      },
    ];
  return [...routes.normal, routes.hard].map((id, index) => ({
    ...createDoor(id, game.floor),
    damage: getGuardianCounterDamage(createDoor(id, game.floor)),
    routeClass:
      index === 2
        ? 'hard'
        : index === 0 || routes.outcome === 'recovery'
        ? 'safe'
        : 'standard',
    locked: index === 2 && !hardAvailable,
    entryCost:
      index === 2 && !hardAvailable && !routes.intro ? HARD_PORTAL_COST : 0,
    canPurchase:
      index === 2 &&
      !hardAvailable &&
      !routes.intro &&
      game.shards >= HARD_PORTAL_COST,
  }));
};
